<template>
    <markdown class="markdown-text">
        <slot/>
    </markdown>
</template>

<script>
  import Markdown from 'vue-markdown-v2';

  export default {
    name: 'MarkdownText',

    components: { Markdown },
  };
</script>

<style scoped>
    .markdown-text >>> h1 {
        margin:         1.25rem 0 2rem;
        padding-bottom: 2rem;
        border-bottom:  1px solid var(--color-gray-lighter);
        line-height:    1;
        font-size:      2.2rem;
        color:          var(--color-primary-shade);
    }

    .markdown-text >>> h2 {
        margin: 2rem 0 0.5rem;
        color:  var(--color-primary);
    }

    .markdown-text >>> h3 {
        margin: 2rem 0 0.5rem;
        color:  var(--color-primary);
    }

    .markdown-text >>> li {
        padding:     4px 0;
        line-height: 1.6;
    }

    .markdown-text >>> a {
        text-decoration: none;
        padding:         2px 0;
        color:           inherit;
        box-shadow:      0 3px 0 0 var(--color-gray-lighter);
        transition:      all 0.125s;
    }

    .markdown-text >>> a:hover {
        color:      var(--color-primary);
        box-shadow: 0 3px 0 0 var(--color-primary-highlight);
    }

    .markdown-text >>> p {
        line-height: 1.6;
        font-size:   1.15rem;
        font-weight: normal;
        margin:      0.75rem 0 0.25rem 0;
    }

    .markdown-text >>> table {
        border-collapse: collapse;
    }

    .markdown-text >>> th,
    .markdown-text >>> td {
        padding: 8px 12px 8px 0;
    }

    .markdown-text >>> tbody > tr:first-child td {
        padding-top: 1rem;
    }

    .markdown-text >>> th {
        text-align:    left;
        font-weight:   500;
        color:         var(--color-gray-darker);
        border-bottom: 3px solid var(--color-gray-lighter);
    }

    .markdown-text >>> blockquote {
        position:      relative;
        margin:        0.5rem 0;
        padding:       0.5rem 1rem 0.5rem 2rem;
        border-left:   1.4rem solid var(--color-primary-highlight);
        border-radius: 0 5px 5px 0;
        background:    var(--color-primary-faded);
    }

    .markdown-text >>> blockquote::before {
        position:    absolute;
        left:        -1.2rem;
        top:         1rem;
        content:     '\e88e';
        font-family: 'Material Icons';
        font-size:   1rem;
        color:       rgba(0, 0, 0, 0.25);
    }

    .markdown-text >>> blockquote p {
        margin:      0;
        color:       rgba(0, 0, 0, 0.6);
        text-shadow: 1px 1px rgba(255, 255, 255, 0.5);
    }

    .markdown-text >>> ul code,
    .markdown-text >>> ol code,
    .markdown-text >>> p code {
        font-size:     1.1rem;
        padding:       4px 6px;
        border-radius: 4px;
        background:    linear-gradient(120deg, rgba(0, 0, 0, 0.05), rgba(10, 64, 30, 0.1));
    }

    .markdown-text >>> a > code {
        padding:       4px 5px;
        border-radius: 4px 4px 0 0;
    }

    .markdown-text >>> pre {
        text-shadow:   1px 1px #ffffff;
        background:    rgba(0, 0, 0, 0.05);
        padding:       1rem;
        border-radius: 4px;
        overflow:      auto;
        max-height:    75vh;
        background:    var(--color-white);
        box-shadow:    inset 0 2px 5px rgba(0, 0, 0, 0.035);
    }

    .markdown-text >>> code::selection,
    .markdown-text >>> code::selection {
        background:  var(--color-primary);
        color:       var(--color-white-bright);
        text-shadow: none;
    }

    .markdown-text >>> code[class*="language-"],
    .markdown-text >>> pre[class*="language-"] {
        color:           #393a34;
        font-family:     "Consolas", "Bitstream Vera Sans Mono", "Courier New", Courier, monospace;
        direction:       ltr;
        text-align:      left;
        white-space:     pre;
        word-spacing:    normal;
        word-break:      normal;
        font-size:       0.95em;
        line-height:     1.2em;

        -moz-tab-size:   4;
        -o-tab-size:     4;
        tab-size:        4;

        -webkit-hyphens: none;
        -moz-hyphens:    none;
        -ms-hyphens:     none;
        hyphens:         none;
    }

    .markdown-text >>> pre[class*="language-"]::-moz-selection,
    .markdown-text >>> pre[class*="language-"] ::-moz-selection,
    .markdown-text >>> code[class*="language-"]::-moz-selection,
    .markdown-text >>> code[class*="language-"] ::-moz-selection {
        text-shadow: none;
        background:  #b3d4fc;
    }

    .markdown-text >>> pre[class*="language-"]::selection,
    .markdown-text >>> pre[class*="language-"] ::selection,
    .markdown-text >>> code[class*="language-"]::selection,
    .markdown-text >>> code[class*="language-"] ::selection {
        text-shadow: none;
        background:  #b3d4fc;
    }

    @media print {
        .markdown-text >>> code[class*="language-"],
        .markdown-text >>> pre[class*="language-"] {
            text-shadow: none;
        }
    }

    /* Code blocks */
    .markdown-text >>> pre[class*="language-"] {
        padding:  1em;
        margin:   1rem 0 2rem;
        overflow: auto;
    }

    .markdown-text >>> :not(pre) > code[class*="language-"],
    .markdown-text >>> pre[class*="language-"] {
        background: var(--color-white);
    }

    /* Inline code */
    .markdown-text >>> :not(pre) > code[class*="language-"] {
        padding:       .1em;
        border-radius: .3em;
        white-space:   normal;
    }

    .markdown-text >>> pre .token.entity,
    .markdown-text >>> pre .token.url,
    .markdown-text >>> pre .token.symbol,
    .markdown-text >>> pre .token.number,
    .markdown-text >>> pre .token.boolean,
    .markdown-text >>> pre .token.variable,
    .markdown-text >>> pre .token.constant,
    .markdown-text >>> pre .token.property,
    .markdown-text >>> pre .token.regex,
    .markdown-text >>> pre .token.inserted {
        color: #36acaa;
    }

    .markdown-text >>> pre .token.punctuation,
    .markdown-text >>> pre .token.operator {
        background: none;
        color:      #727983;
    }

    .markdown-text >>> pre .token.atrule,
    .markdown-text >>> pre .token.keyword,
    .markdown-text >>> pre .token.attr-name,
    .markdown-text >>> pre .language-autohotkey .token.selector {
        color: #00a4db;
    }

    .markdown-text >>> pre .token.function,
    .markdown-text >>> pre .token.deleted,
    .markdown-text >>> pre .language-autohotkey .token.tag {
        color: #9a050f;
    }

    .markdown-text >>> pre .token.tag,
    .markdown-text >>> pre .token.selector,
    .markdown-text >>> pre .language-autohotkey .token.keyword {
        color: #00009f;
    }

    .markdown-text >>> pre .token.important,
    .markdown-text >>> pre .token.function,
    .markdown-text >>> pre .token.bold {
        font-weight: bold;
    }

    .markdown-text >>> pre .token.italic {
        font-style: italic;
    }
</style>
